import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useParams } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";

const createEmptyItem = () => ({
  Type: "Iraab",
  Name: "",
  Title: "",
  points: 1,
  skilltype: "",
  itemcat: "",
  Iraab: { id: 1, Rationale: "", Value: "", Direction: "RTL" },
  version: "v1",
});

const IraabItemDetail = () => {
  const { itemId } = useParams();
  const isNew = !itemId || itemId === "new";

  const [item, setItem] = useState(createEmptyItem());
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false); // ✅ added

  // 🟢 Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [taxonomyResponse, itemResponse] = await Promise.all([
          ItemService.fetchTaxonomy("arabic"),
          isNew ? Promise.resolve(null) : ItemService.fetchItemById(itemId),
        ]);
        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        if (itemResponse?.item) {
          const loadedItem = itemResponse.item;
          setItem({
            ...loadedItem,
            Iraab: {
              id: 1,
              Rationale: loadedItem.Iraab?.Rationale || "",
              Value: loadedItem.Iraab?.Value || "",
              Direction: loadedItem.Iraab?.Direction || "RTL",
            },
          });
        }
      } catch (err) {
        console.error("Failed to load Iraab item:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [itemId, isNew]);

  const handleFieldChange = (key, value) =>
    setItem((prev) => ({ ...prev, [key]: value }));

  const handleIraabChange = (field, value) =>
    setItem((prev) => ({
      ...prev,
      Iraab: { ...prev.Iraab, [field]: value },
    }));

  // 🟢 Save function
  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();
    const payload = {
      ...item,
      dateUpdated: timestamp,
      dateCreated: item.dateCreated || timestamp,
    };

    try {
      setSaving(true);
      await ItemService.saveItem(payload, isNew ? undefined : itemId);
      setSaveSuccess(true);
      setSaveError(null);
    } catch (err) {
      console.error("Save error:", err);
      setSaveError("Failed to save Iraab item.");
    } finally {
      setSaving(false);
    }
  }, [item, itemId, isNew]);

  const jsonPreview = useMemo(() => JSON.stringify(item, null, 2), [item]);

  if (loading)
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading Iraab item...</Typography>
      </Paper>
    );

  // === IRAAB PREVIEW CONTENT (inside dialog) ===
  const IraabPreviewContent = () => {
    const [selectedWord, setSelectedWord] = useState(null);
    const [answers, setAnswers] = useState({});

    if (!item?.Iraab?.Value)
      return <Typography>No Iraab text entered yet.</Typography>;

    const harakaatOptions = ["َ", "ُ", "ِ", "ْ"];
    const words = item.Iraab.Value.split(" ").map((word) => {
      const harakaatPattern = /[ًٌٍَُِْ]/;
      const lastChar = word.slice(-1);
      const hasHaraka = harakaatPattern.test(lastChar);
      const cleanedWord = hasHaraka ? word.slice(0, -1) : word;
      return { word: cleanedWord, hasHaraka };
    });

    const handleSelectWord = (index, hasHaraka) => {
      if (!hasHaraka) return;
      setSelectedWord(selectedWord === index ? null : index);
    };

    const handleChooseHaraka = (haraka) => {
      if (selectedWord !== null) {
        setAnswers((prev) => ({
          ...prev,
          [selectedWord]: `${words[selectedWord].word}${haraka}`,
        }));
      }
      setSelectedWord(null);
    };

    return (
      <Box
        sx={{
          direction: item.Iraab.Direction === "RTL" ? "rtl" : "ltr",
          textAlign: item.Iraab.Direction === "RTL" ? "right" : "left",
          fontFamily: "Traditional Arabic, serif",
          fontSize: "1.5rem",
          border: "1px solid #ddd",
          borderRadius: "8px",
          p: 2,
          backgroundColor: "#fff",
        }}
      >
        {words.map(({ word, hasHaraka }, index) => (
          <Box
            key={index}
            display="inline-block"
            textAlign="center"
            m={0.5}
            position="relative"
          >
            <Typography
              onClick={() => handleSelectWord(index, hasHaraka)}
              sx={{
                backgroundColor: hasHaraka ? "#fff" : "#f0f0f0",
                cursor: hasHaraka ? "pointer" : "default",
                opacity: hasHaraka ? 1 : 0.6,
                borderRadius: "4px",
                px: 1,
                userSelect: "none",
              }}
            >
              {answers[index] || word}
            </Typography>
          </Box>
        ))}

        {selectedWord !== null && (
          <Box
            position="fixed"
            top={0}
            left={0}
            width="100vw"
            height="100vh"
            bgcolor="rgba(0,0,0,0.4)"
            zIndex={1300}
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={() => setSelectedWord(null)}
          >
            <Box
              bgcolor="white"
              borderRadius="8px"
              p={2}
              boxShadow={5}
              onClick={(e) => e.stopPropagation()}
            >
              <Stack spacing={1}>
                {harakaatOptions.map((haraka, idx) => (
                  <Typography
                    key={idx}
                    onClick={() => handleChooseHaraka(haraka)}
                    sx={{
                      backgroundColor: "#1976d2",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                      textAlign: "center",
                    }}
                  >
                    {words[selectedWord].word + haraka}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="Iraab" color="secondary" />
              <Typography variant="h5" fontWeight={600}>
                {isNew ? "Create Iraab Item" : "Edit Iraab Item"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => setPreviewDialogOpen(true)}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                onClick={() => setJsonDialogOpen(true)}
              >
                View JSON
              </Button>
            </Stack>
          </Stack>

          {saveSuccess && (
            <Alert severity="success" onClose={() => setSaveSuccess(false)}>
              Iraab item saved successfully!
            </Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          <Divider />

          {/* === Basic Info === */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                fullWidth
                required
                value={item.Name}
                onChange={(e) => handleFieldChange("Name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Points"
                type="number"
                fullWidth
                value={item.points}
                onChange={(e) => handleFieldChange("points", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  label="Skill Type"
                  onChange={(e) =>
                    handleFieldChange(
                      "skilltype",
                      normalizeSkillTypeValue(e.target.value)
                    )
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  label="Item Category"
                  onChange={(e) => handleFieldChange("itemcat", e.target.value)}
                >
                  {taxonomyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider />

          <Typography variant="subtitle1" fontWeight={600}>
            Instructions (Title)
          </Typography>
          <ReactQuill
            value={item.Title}
            onChange={(val) => handleFieldChange("Title", val)}
          />

          <Divider />

          <Typography variant="subtitle1" fontWeight={600}>
            Iraab Text
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={item.Iraab.Direction === "RTL"}
                onChange={() =>
                  handleIraabChange(
                    "Direction",
                    item.Iraab.Direction === "RTL" ? "LTR" : "RTL"
                  )
                }
              />
            }
            label="Direction"
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            value={item.Iraab.Value}
            onChange={(e) => handleIraabChange("Value", e.target.value)}
            placeholder="Enter Arabic sentence(s)..."
            InputProps={{
              sx: {
                direction: item.Iraab.Direction === "RTL" ? "rtl" : "ltr",
                textAlign: item.Iraab.Direction === "RTL" ? "right" : "left",
                fontFamily: "Traditional Arabic, serif",
                fontSize: "1.2rem",
              },
            }}
          />

          <Typography variant="subtitle1" fontWeight={600}>
            Rationale
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={item.Iraab.Rationale}
            onChange={(e) => handleIraabChange("Rationale", e.target.value)}
          />

          <Stack direction="row" justifyContent="flex-end">
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Item"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* === JSON Dialog === */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* === Preview Dialog === */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Iraab Preview</DialogTitle>
        <DialogContent dividers>
          <IraabPreviewContent />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default IraabItemDetail;
